import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableRow,
  Typography, Autocomplete, Tooltip, Collapse, Snackbar, Alert
} from '@mui/material';
import { Add, Upload, ExpandMore, ExpandLess, Delete, ShoppingCart, ArrowUpward, ArrowDownward, FilterAltOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { RootState } from '../store';
import { Opportunity } from '../types';
import { useColumnConfig, ColumnDefinition } from '../hooks/useColumnConfig';
import DraggableTableHeader from '../components/DraggableTableHeader';
import { useSavedFilterViews, SavedFilterView } from '../hooks/useSavedFilterViews';
import { SavedViewsTabs } from '../components/SavedViewsTabs';
import TextField from '../components/common/TextField';

// Types with colors
const TYPES: { value: string; color: string }[] = [
  { value: 'Reposición', color: '#2196F3' },
  { value: 'Compra', color: '#4CAF50' },
  { value: 'Online', color: '#9C27B0' },
  { value: 'Fason', color: '#FF9800' },
  { value: 'Evento', color: '#E91E63' },
  { value: 'New Logo', color: '#00BCD4' },
  { value: 'Influencer', color: '#673AB7' },
  { value: 'Consignación', color: '#795548' },
  { value: 'Interna', color: '#607D8B' },
  { value: 'Marketing', color: '#F44336' },
  { value: 'Lead-Online', color: '#3F51B5' },
  { value: 'Exportación', color: '#009688' },
  { value: 'Materia Prima', color: '#8BC34A' },
];

const STAGES: { value: string; color: string }[] = [
  { value: 'Proyección', color: '#FFC107' },
  { value: 'Ganado', color: '#4CAF50' },
  { value: 'Perdido', color: '#F44336' },
  { value: 'Negociando', color: '#03A9F4' },
  { value: 'Cotización Pendiente', color: '#FF9800' },
];

const getTypeColor = (type?: string) => TYPES.find(t => t.value === type)?.color || '#9E9E9E';
const getStageColor = (stage: string) => STAGES.find(s => s.value === stage)?.color || '#9E9E9E';

const fmtCurrency = (v?: number) => v != null ? `$ ${Math.round(v).toLocaleString('es-AR')}` : '—';
// Date-only fields round-trip as strings — never feed them to `new Date()` (UTC→local drift).
const fmtDate = (d?: string) => {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};
const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fmtMonth = (d: string) => {
  const [y, m] = d.split('-');
  return `${MONTHS_ES[Number(m) - 1]} ${y}`;
};

// ─── Saved views (tabs) ──────────────────────────────────────────────────────

const defaultViews: SavedFilterView[] = [
  { id: 'proyeccion', name: 'Proyección', isDefault: true, filters: { state: 'Proyección' }, createdAt: '' },
  { id: 'negociando', name: 'Negociando', isDefault: true, filters: { state: 'Negociando' }, createdAt: '' },
  { id: 'cotizacion-pendiente', name: 'Cotización Pendiente', isDefault: true, filters: { state: 'Cotización Pendiente' }, createdAt: '' },
  { id: 'ganado', name: 'Ganado', isDefault: true, filters: { state: 'Ganado' }, createdAt: '' },
];

const OPPORTUNITY_COLUMNS: ColumnDefinition[] = [
  { id: 'title', label: 'Título' },
  { id: 'customerName', label: 'Cliente' },
  { id: 'owner', label: 'Vendedor' },
  { id: 'type', label: 'Tipo' },
  { id: 'state', label: 'Etapa' },
  { id: 'nextStep', label: 'Próximo paso' },
  { id: 'estimatedValue', label: 'Valor', align: 'right' },
  { id: 'probabilityPct', label: 'Prob %', align: 'right' },
  { id: 'estimatedCloseDate', label: 'Fecha Cierre' },
];

export const OpportunitiesPage: React.FC = () => {
  const { user } = useSelector((s: RootState) => s.auth);
  const navigate = useNavigate();
  const isSeller = ['seller', 'vendedormarcapropia'].includes((user?.role || '').toLowerCase());

  const [items, setItems] = useState<Opportunity[]>([]);
  const [customers, setCustomers] = useState<{ id: number; legalName: string }[]>([]);
  const [products, setProducts] = useState<{ id: number; name: string; sku: string; price: number }[]>([]);
  const [employees, setEmployees] = useState<string[]>([]);
  const [owner, setOwner] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [projection, setProjection] = useState<any>({});
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<any>({
    title: '', type: '', state: 'Proyección', owner: '', estimatedValue: 0,
    probabilityPct: 0, estimatedCloseDate: '', paymentDueDate: '', paymentDate: '',
    actualDeliveryDate: '', notes: '', nextStep: '', nextStepAt: '', customerId: null
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOpp, setDetailOpp] = useState<Opportunity | null>(null);
  type ProductOption = { id: number; name: string; sku: string; price: number };
  const [newItem, setNewItem] = useState<{ productId: number; quantity: number; unitPrice: number; discountPct: number; ivaRate: number; selected: ProductOption | null }>({ productId: 0, quantity: 1, unitPrice: 0, discountPct: 0, ivaRate: 0, selected: null });
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  const { visibleColumns, columns, columnConfigs, reorderColumns, toggleVisibility, resetToDefaults, isDefaultOrder } =
    useColumnConfig({ storageKey: 'opportunities-page-columns-v2', defaultColumns: OPPORTUNITY_COLUMNS });

  const { views, activeViewId, activeView, saveView, deleteView, renameView, setActiveView } =
    useSavedFilterViews({ storageKey: 'opportunities-page-views', defaultViews: defaultViews as any });

  const handleSaveView = () => {
    setSaveViewDialogOpen(true);
    setNewViewName('');
  };

  const handleSaveViewConfirm = () => {
    if (newViewName.trim()) {
      saveView(newViewName.trim(), { state: stateFilter, owner, type: typeFilter });
    }
    setSaveViewDialogOpen(false);
    setNewViewName('');
  };

  // Apply view filters when active view changes
  useEffect(() => {
    if (activeView?.filters) {
      if (activeView.filters.state !== undefined) setStateFilter(activeView.filters.state);
      if (activeView.filters.owner !== undefined) setOwner(activeView.filters.owner);
      if (activeView.filters.type !== undefined) setTypeFilter(activeView.filters.type);
    }
  }, [activeViewId]);

  // Inline status change
  const handleInlineStatusChange = async (oppId: number, newState: string) => {
    try {
      const opp = items.find(o => o.id === oppId);
      if (!opp) return;
      await api.updateOpportunity(oppId, { ...opp, state: newState });
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error al cambiar etapa');
    }
  };

  // Load data
  const load = useCallback(async () => {
    const [res, pRes] = await Promise.all([
      api.listOpportunities({ state: stateFilter || undefined, owner: owner || undefined, type: typeFilter || undefined }),
      api.getOpportunityProjection()
    ]);
    // Hide won opportunities that have been converted to sales
    setItems((res.items || []).filter((o: Opportunity) => !(o.state === 'Ganado' && o.convertedOrderId)));
    setProjection(pRes);
  }, [stateFilter, owner, typeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      api.getCustomers({ pageSize: 1000 }).then(r => setCustomers((r.customers || []).map((c: any) => ({ id: c.id, legalName: c.legalName })))),
      api.getUsers().then(r => setEmployees((r.items || []).map((u: any) => u.name).filter(Boolean))),
    ]).catch(err => { console.error('OpportunitiesPage: failed to load reference data', err); });
  }, []);

  // Product Autocomplete: server-side search (handles catalogs > 1000 SKUs).
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [productSearch, setProductSearch] = useState('');
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => {
      setProductSearchLoading(true);
      api.getProducts({ page: 1, pageSize: 30, search: productSearch || undefined })
        .then(r => setProducts((r.products || []).map((p: any) => ({ id: p.id, name: p.name, sku: p.sku, price: p.price }))))
        .catch(err => console.error('Product search failed', err))
        .finally(() => setProductSearchLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [productSearch]);

  // Group by month
  const monthGroups = useMemo(() => {
    const groups = new Map<string, Opportunity[]>();
    items.forEach(o => {
      const key = o.estimatedCloseDate ? o.estimatedCloseDate.slice(0, 7) : 'sin-fecha';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(o);
    });
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  // Arranca todo colapsado y solo cambia por accion explicita del usuario (toggleMonth).
  // Antes se auto-expandia cualquier mes "nunca visto", pero cambiar de filtro alcanza
  // para revelar meses nuevos bajo ese filtro puntual -- eso hacia que tocar los filtros
  // pareciera desplegar todo de nuevo cada vez.
  const toggleMonth = (key: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!form.title) return;
    const payload = {
      ...form,
      estimatedValue: Number(form.estimatedValue || 0),
      probabilityPct: Number(form.probabilityPct || 0),
      estimatedCloseDate: form.estimatedCloseDate || undefined,
      nextStepAt: form.nextStepAt || undefined,
    };
    try {
      if (editingId) {
        await api.updateOpportunity(editingId, payload);
        setOpen(false);
        setEditingId(null);
        await load();
      } else {
        await api.createOpportunity(payload);
        setOpen(false);
        setEditingId(null);
        await load();
        setToast({ open: true, message: 'Oportunidad creada', severity: 'success' });
      }
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error al guardar la oportunidad');
    }
  };

  const startEdit = (o: Opportunity) => {
    setEditingId(o.id);
    setForm({
      title: o.title, type: o.type || '', state: o.state, owner: o.owner || '',
      estimatedValue: o.estimatedValue, probabilityPct: o.probabilityPct,
      estimatedCloseDate: o.estimatedCloseDate?.slice(0, 10) || '', notes: o.notes || '',
      nextStep: o.nextStep || '', nextStepAt: o.nextStepAt?.slice(0, 10) || '',
      customerId: o.customerId || null, competitors: o.competitors || '',
      deliveryCondition: o.deliveryCondition || '', paymentMethod: o.paymentMethod || '',
      commissionStatus: o.commissionStatus || '', commissionPct: o.commissionPct || 0,
      paymentStatus: o.paymentStatus || '', deliveryStatus: o.deliveryStatus || '',
      paidAmount: o.paidAmount || 0, isReconciled: o.isReconciled || false,
      receivedConfirmation: o.receivedConfirmation || false,
      actualDeliveryDate: o.actualDeliveryDate?.slice(0, 10) || '',
      paymentDueDate: o.paymentDueDate?.slice(0, 10) || '',
      paymentDate: o.paymentDate?.slice(0, 10) || '',
      finalInvoiceAmount: o.finalInvoiceAmount || 0,
      offerReference: o.offerReference || '', receiptNumber: o.receiptNumber || '',
      deliverySlipReference: o.deliverySlipReference || '',
    });
    setOpen(true);
  };

  const handleConvertToOrder = async (opp: Opportunity) => {
    if (!window.confirm(`¿Convertir "${opp.title}" a venta? Esta acción creará una nueva venta.`)) return;
    try {
      await api.convertOpportunityToOrder(opp.id);
      await load();
      setDetailOpen(false);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Error al convertir');
    }
  };

  const openDetail = (o: Opportunity) => {
    setDetailOpp(o);
    setDetailOpen(true);
  };

  const addItemToOpp = async () => {
    if (!detailOpp || !newItem.productId) return;
    const price = newItem.unitPrice || newItem.selected?.price || 0;
    await api.addOpportunityItem(detailOpp.id, { productId: newItem.productId, quantity: newItem.quantity, unitPrice: price, discountPct: newItem.discountPct, ivaRate: newItem.ivaRate });
    const refreshed = await api.listOpportunities({});
    const updated = (refreshed.items || []).find((o: Opportunity) => o.id === detailOpp.id);
    if (updated) setDetailOpp(updated);
    setNewItem({ productId: 0, quantity: 1, unitPrice: 0, discountPct: 0, ivaRate: 0, selected: null });
    await load();
  };

  const removeItemFromOpp = async (itemId: number) => {
    if (!detailOpp) return;
    await api.removeOpportunityItem(detailOpp.id, itemId);
    const refreshed = await api.listOpportunities({});
    const updated = (refreshed.items || []).find((o: Opportunity) => o.id === detailOpp.id);
    if (updated) setDetailOpp(updated);
    await load();
  };

  const renderCell = (o: Opportunity, colId: string) => {
    switch (colId) {
      case 'title':
        return <TableCell key={colId} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }} onClick={() => openDetail(o)}>{o.title}</TableCell>;
      case 'customerName':
        return <TableCell key={colId}>{o.customerName || '—'}</TableCell>;
      case 'owner':
        return <TableCell key={colId}>
          {o.owner ? <Tooltip title={o.owner}><Chip size="small" label={o.owner.split(/[\s,]+/).map(w => w[0]).join('').toUpperCase().slice(0, 3)} /></Tooltip> : '—'}
        </TableCell>;
      case 'type':
        return <TableCell key={colId}>
          {o.type ? <Chip size="small" label={o.type} sx={{ bgcolor: getTypeColor(o.type), color: '#fff' }} /> : '—'}
        </TableCell>;
      case 'state':
        return <TableCell key={colId}>
          <Select
            size="small"
            value={o.state}
            onChange={(e) => handleInlineStatusChange(o.id, e.target.value)}
            variant="standard"
            disableUnderline
            renderValue={(val) => <Chip size="small" label={val} sx={{ bgcolor: getStageColor(val as string), color: '#fff', cursor: 'pointer' }} />}
            sx={{ minWidth: 100 }}
          >
            {STAGES.map(s => (
              <MenuItem key={s.value} value={s.value}>
                <Chip size="small" label={s.value} sx={{ bgcolor: s.color, color: '#fff', mr: 1 }} /> {s.value}
              </MenuItem>
            ))}
          </Select>
        </TableCell>;
      case 'nextStep':
        return <TableCell key={colId}>
          {o.nextStep ? (
            <Tooltip title={o.nextStep}>
              <Box>
                <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.nextStep}</Typography>
                {o.nextStepAt && <Typography variant="caption" color="text.secondary">{fmtDate(o.nextStepAt)}</Typography>}
              </Box>
            </Tooltip>
          ) : '—'}
        </TableCell>;
      case 'estimatedValue':
        return <TableCell key={colId} align="right">{fmtCurrency(o.estimatedValue)}</TableCell>;
      case 'probabilityPct':
        return <TableCell key={colId} align="right">{o.probabilityPct}%</TableCell>;
      case 'estimatedCloseDate':
        return <TableCell key={colId}>{fmtDate(o.estimatedCloseDate)}</TableCell>;
      default:
        return <TableCell key={colId}>—</TableCell>;
    }
  };

  // Orden client-side dentro de cada grupo de mes (el backend ya viene agrupado por fecha).
  const SORT_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'Sin orden' },
    { value: 'estimatedValue', label: 'Valor' },
    { value: 'estimatedCloseDate', label: 'Fecha de cierre' },
    { value: 'probabilityPct', label: 'Probabilidad' },
    { value: 'customerName', label: 'Cliente' },
    { value: 'title', label: 'Título' },
    { value: 'owner', label: 'Vendedor' },
    { value: 'state', label: 'Etapa' },
    { value: 'nextStepAt', label: 'Fecha próximo paso' },
  ];
  const sortRows = (rows: Opportunity[]) => {
    if (!sortKey) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = (a as any)[sortKey];
      const bv = (b as any)[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // vacíos al final
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), 'es', { numeric: true }) * dir;
    });
  };

  const clearFilters = () => {
    setActiveView('');
    setStateFilter('');
    setOwner('');
    setTypeFilter('');
    setSortKey('');
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>Pipeline de Oportunidades</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
          {!isSeller && (
            <Autocomplete
              size="small"
              freeSolo
              options={employees}
              value={owner}
              onInputChange={(_, v) => setOwner(v)}
              renderInput={(params) => <TextField {...params} label="Vendedor" sx={{ minWidth: 160 }} />}
            />
          )}
          <TextField size="small" select label="Tipo" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Todos</MenuItem>
            {TYPES.map(t => <MenuItem key={t.value} value={t.value}><Chip size="small" label={t.value} sx={{ bgcolor: t.color, color: '#fff', mr: 1 }} /> {t.value}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Etapa" value={stateFilter} onChange={e => setStateFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Todas</MenuItem>
            {STAGES.map(s => <MenuItem key={s.value} value={s.value}><Chip size="small" label={s.value} sx={{ bgcolor: s.color, color: '#fff', mr: 1 }} /> {s.value}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TextField size="small" select label="Ordenar por" value={sortKey} onChange={e => setSortKey(e.target.value)} sx={{ minWidth: 150 }}>
              {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <Tooltip title={sortDir === 'asc' ? 'Ascendente' : 'Descendente'}>
              <span>
                <IconButton size="small" disabled={!sortKey} onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                  {sortDir === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Button variant="outlined" onClick={load}>Filtrar</Button>
          <Tooltip title="Limpiar filtros y orden">
            <Button variant="text" startIcon={<FilterAltOff />} onClick={clearFilters}>Limpiar</Button>
          </Tooltip>
          {!isSeller && (
            <Button startIcon={<Upload />} variant="outlined" onClick={() => navigate('/opportunities/import')}>Importar</Button>
          )}
          <Button startIcon={<Add />} variant="contained" onClick={() => { setEditingId(null); setForm({ title: '', type: '', state: 'Proyección', owner: isSeller ? user?.name || '' : '', estimatedValue: 0, probabilityPct: 0, estimatedCloseDate: '', notes: '', nextStep: '', nextStepAt: '', customerId: null }); setOpen(true); }}>Nueva</Button>
        </Stack>
      </Stack>

      {/* Projection summary */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary">Proyección {projection.month}</Typography>
            <Stack direction="row" spacing={3}>
              <Box><Typography variant="body2" color="text.secondary">Ganadas</Typography><Typography fontWeight={600}>{fmtCurrency(projection.wonSum)}</Typography></Box>
              <Box><Typography variant="body2" color="text.secondary">Proyectadas</Typography><Typography fontWeight={600}>{fmtCurrency(projection.projectedSum)}</Typography></Box>
              <Box><Typography variant="body2" color="text.secondary">Esperanza</Typography><Typography fontWeight={600} color="primary">{fmtCurrency(projection.expectedValue)}</Typography></Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* View tabs */}
      <SavedViewsTabs
        views={views}
        activeViewId={activeViewId}
        onSelectView={(id) => { setActiveView(id); if (!id) { setStateFilter(''); setOwner(''); } }}
        onSaveCurrentView={handleSaveView}
        onDeleteView={deleteView}
        onRenameView={renameView}
        showAllTab={true}
        allTabLabel="Todas"
      />

      {/* Month-grouped table */}
      {monthGroups.map(([monthKey, monthItems]) => {
        const isExpanded = expandedMonths.has(monthKey);
        // "Estimado"/Ponderado tiene que sumar toda oportunidad todavia abierta (Proyeccion,
        // Negociando, Cotizacion Pendiente), no solo la etapa literal "Proyeccion" -- si no,
        // una oportunidad en "Negociando" (que sigue siendo una proyeccion) quedaba afuera del
        // total sin que se notara.
        const openItems = monthItems.filter(o => o.state !== 'Ganado' && o.state !== 'Perdido');
        const totalProj = openItems.reduce((s, o) => s + o.estimatedValue, 0);
        const totalWeighted = openItems.reduce((s, o) => s + o.estimatedValue * (o.probabilityPct / 100), 0);
        const totalGanado = monthItems.filter(o => o.state === 'Ganado').reduce((s, o) => s + o.estimatedValue, 0);

        return (
          <Card key={monthKey} sx={{ mb: 1 }}>
            <Box
              sx={{ px: 2, py: 1, bgcolor: 'grey.50', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onClick={() => toggleMonth(monthKey)}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                <Typography fontWeight={600}>{monthKey === 'sin-fecha' ? 'Sin fecha' : fmtMonth(monthKey)}</Typography>
                <Chip size="small" label={`${monthItems.length} opp`} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2">Ganado: <b>{fmtCurrency(totalGanado)}</b></Typography>
                <Typography variant="body2">Estimado: <b>{fmtCurrency(totalProj)}</b></Typography>
                <Typography variant="body2" color="primary">Ponderado: <b>{fmtCurrency(totalWeighted)}</b></Typography>
              </Stack>
            </Box>
            <Collapse in={isExpanded}>
              <TableContainer>
                <Table size="small">
                  <DraggableTableHeader
                    columns={columns}
                    visibleColumns={visibleColumns}
                    columnConfigs={columnConfigs}
                    onReorder={reorderColumns}
                    onToggleVisibility={toggleVisibility}
                    onResetToDefaults={resetToDefaults}
                    isDefaultOrder={isDefaultOrder}
                    trailingCell={<TableCell>Acciones</TableCell>}
                  />
                  <TableBody>
                    {sortRows(monthItems).map(o => (
                      <TableRow key={o.id} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        {visibleColumns.map(col => renderCell(o, col.id))}
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" onClick={() => startEdit(o)}>Editar</Button>
                            {o.state !== 'Ganado' && o.state !== 'Perdido' && !o.convertedOrderId && (
                              <Button size="small" color="success" startIcon={<ShoppingCart />} onClick={() => handleConvertToOrder(o)}>Ganar</Button>
                            )}
                            {!o.convertedOrderId && (
                              <IconButton size="small" color="error" onClick={async () => {
                                if (!window.confirm('¿Eliminar esta oportunidad?')) return;
                                try {
                                  await api.deleteOpportunity(o.id);
                                  await load();
                                } catch (e: any) {
                                  alert(e?.response?.data?.message || 'Error al eliminar');
                                }
                              }}><Delete fontSize="small" /></IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Card>
        );
      })}
      {items.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Sin oportunidades registradas.</Typography>}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => { setOpen(false); setEditingId(null); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Editar Oportunidad' : 'Nueva Oportunidad'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Título" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} fullWidth required />
              <Autocomplete
                size="small"
                options={customers}
                getOptionLabel={(o) => typeof o === 'string' ? o : o.legalName}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={customers.find(c => c.id === form.customerId) || null}
                onChange={(_, v) => setForm({ ...form, customerId: v?.id || null })}
                selectOnFocus
                handleHomeEndKeys
                filterOptions={(opts, state) => {
                  const selected = customers.find(c => c.id === form.customerId);
                  const input = state.inputValue.trim().toLowerCase();
                  if (!input || (selected && input === selected.legalName.toLowerCase())) return opts;
                  return opts.filter(o => o.legalName.toLowerCase().includes(input));
                }}
                renderInput={(params) => <TextField {...params} label="Cliente" />}
                sx={{ minWidth: 200 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete
                size="small"
                freeSolo
                options={employees}
                value={form.owner}
                onInputChange={(_, v) => setForm({ ...form, owner: v })}
                renderInput={(params) => <TextField {...params} label="Vendedor" />}
                disabled={isSeller}
                sx={{ flex: 1 }}
              />
              <TextField select label="Tipo" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} sx={{ minWidth: 180 }}>
                <MenuItem value="">Sin tipo</MenuItem>
                {TYPES.map(t => <MenuItem key={t.value} value={t.value}><Chip size="small" sx={{ bgcolor: t.color, color: '#fff', mr: 1 }} label=" " /> {t.value}</MenuItem>)}
              </TextField>
              <TextField select label="Etapa" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} sx={{ minWidth: 180 }}>
                {STAGES.map(s => <MenuItem key={s.value} value={s.value}><Chip size="small" sx={{ bgcolor: s.color, color: '#fff', mr: 1 }} label=" " /> {s.value}</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Valor estimado" type="number" value={form.estimatedValue} onChange={e => setForm({ ...form, estimatedValue: e.target.value })} fullWidth />
              <TextField label="Prob %" type="number" value={form.probabilityPct} onChange={e => setForm({ ...form, probabilityPct: e.target.value })} fullWidth />
              <TextField label="Fecha de cierre" type="date" InputLabelProps={{ shrink: true }} value={form.estimatedCloseDate} onChange={e => setForm({ ...form, estimatedCloseDate: e.target.value })} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Vto. pago" type="date" InputLabelProps={{ shrink: true }} value={form.paymentDueDate || ''} onChange={e => setForm({ ...form, paymentDueDate: e.target.value })} fullWidth />
              <TextField label="Fecha de pago" type="date" InputLabelProps={{ shrink: true }} value={form.paymentDate || ''} onChange={e => setForm({ ...form, paymentDate: e.target.value })} fullWidth />
              <TextField label="Entrega real" type="date" InputLabelProps={{ shrink: true }} value={form.actualDeliveryDate || ''} onChange={e => setForm({ ...form, actualDeliveryDate: e.target.value })} fullWidth />
            </Stack>
            {editingId && (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField select label="Condición Pago" value={form.paymentMethod || ''} onChange={e => setForm({ ...form, paymentMethod: e.target.value })} fullWidth>
                    {['No Aplica', 'Contado', 'Cuenta Corriente', 'Cheque', 'Transferencia'].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </TextField>
                </Stack>
              </>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Próximo paso" value={form.nextStep || ''} onChange={e => setForm({ ...form, nextStep: e.target.value })} fullWidth inputProps={{ maxLength: 300 }} placeholder="Ej. Llamar para cerrar cotización" />
              <TextField label="Fecha próximo paso" type="date" InputLabelProps={{ shrink: true }} value={form.nextStepAt || ''} onChange={e => setForm({ ...form, nextStepAt: e.target.value })} sx={{ minWidth: 200 }} />
            </Stack>
            <TextField label="Notas" multiline minRows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); setEditingId(null); }}>Cancelar</Button>
          <Button variant="contained" onClick={save}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog with Products */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{detailOpp?.title}</Typography>
            <Stack direction="row" spacing={1}>
              {detailOpp && !detailOpp.convertedOrderId && (
                <Button variant="contained" color="success" startIcon={<ShoppingCart />} onClick={() => handleConvertToOrder(detailOpp)}>
                  {detailOpp.state === 'Ganado' ? 'Crear Venta' : 'Marcar Ganado → Crear Venta'}
                </Button>
              )}
              {detailOpp?.convertedOrderId && (
                <Chip label={`Venta #${detailOpp.convertedOrderId}`} color="success" />
              )}
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {detailOpp && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Typography variant="body2">Cliente: <b>{detailOpp.customerName || '—'}</b></Typography>
                <Typography variant="body2">Vendedor: <b>{detailOpp.owner || '—'}</b></Typography>
                <Chip size="small" label={detailOpp.type || 'Sin tipo'} sx={{ bgcolor: getTypeColor(detailOpp.type), color: '#fff' }} />
                <Chip size="small" label={detailOpp.state} sx={{ bgcolor: getStageColor(detailOpp.state), color: '#fff' }} />
                <Typography variant="body2">Valor: <b>{fmtCurrency(detailOpp.estimatedValue)}</b></Typography>
                <Typography variant="body2">Prob: <b>{detailOpp.probabilityPct}%</b></Typography>
                <Typography variant="body2">Fecha cierre: <b>{fmtDate(detailOpp.estimatedCloseDate)}</b></Typography>
              </Stack>

              {/* Products section */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Productos</Typography>
              {(detailOpp.items || []).length > 0 && (() => {
                const items = detailOpp.items || [];
                const totalNeto = items.reduce((s, i) => {
                  const net = i.unitPrice * (1 - (i.discountPct || 0) / 100);
                  return s + i.quantity * net;
                }, 0);
                const totalBruto = items.reduce((s, i) => s + i.lineTotal, 0);
                const totalCost = items.reduce((s, i) => s + i.quantity * (i.unitCost || 0), 0);
                const margin = totalNeto - totalCost;
                return (
                  <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
                    <Typography variant="body2">Total Neto: <b>{fmtCurrency(totalNeto)}</b></Typography>
                    <Typography variant="body2">Total Bruto (c/IVA): <b>{fmtCurrency(totalBruto)}</b></Typography>
                    <Typography variant="body2">Costo: <b>{fmtCurrency(totalCost)}</b></Typography>
                    <Typography variant="body2" color={margin >= 0 ? 'success.main' : 'error.main'}>Margen: <b>{fmtCurrency(margin)}</b></Typography>
                  </Stack>
                );
              })()}
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><b>Producto</b></TableCell>
                    <TableCell><b>SKU</b></TableCell>
                    <TableCell align="right"><b>Cant</b></TableCell>
                    <TableCell align="right"><b>Precio Unit</b></TableCell>
                    <TableCell align="right"><b>Desc %</b></TableCell>
                    <TableCell align="right"><b>IVA</b></TableCell>
                    <TableCell align="right"><b>Total</b></TableCell>
                    <TableCell />
                  </TableRow>
                  {(detailOpp.items || []).map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell>{item.sku || '—'}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{fmtCurrency(item.unitPrice)}</TableCell>
                      <TableCell align="right">{item.discountPct ? `${item.discountPct}%` : '—'}</TableCell>
                      <TableCell align="right">{item.ivaRate ? `${item.ivaRate}%` : '0%'}</TableCell>
                      <TableCell align="right">{fmtCurrency(item.lineTotal)}</TableCell>
                      <TableCell><IconButton size="small" onClick={() => removeItemFromOpp(item.id)}><Delete fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                  {(detailOpp.items || []).length === 0 && (
                    <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">Sin productos</Typography></TableCell></TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Add product */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Autocomplete
                  size="small"
                  options={products}
                  getOptionLabel={p => `${p.sku} - ${p.name}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={newItem.selected || null}
                  loading={productSearchLoading}
                  onInputChange={(_, v, reason) => { if (reason === 'input') setProductSearch(v); }}
                  onChange={(_, v) => setNewItem({ ...newItem, selected: v || null, productId: v?.id || 0, unitPrice: v?.price || 0 })}
                  filterOptions={x => x}
                  renderInput={params => <TextField {...params} label="Producto" />}
                  sx={{ minWidth: 250 }}
                />
                <TextField size="small" label="Cant" type="number" value={newItem.quantity} onChange={e => setNewItem({ ...newItem, quantity: Number(e.target.value) })} sx={{ width: 80 }} />
                <TextField size="small" label="Precio" type="number" value={newItem.unitPrice} onChange={e => setNewItem({ ...newItem, unitPrice: Number(e.target.value) })} sx={{ width: 100 }} />
                <TextField size="small" label="Desc %" type="number" value={newItem.discountPct} onChange={e => setNewItem({ ...newItem, discountPct: Number(e.target.value) })} sx={{ width: 80 }} inputProps={{ min: 0, max: 100 }} />
                <TextField size="small" select label="IVA" value={newItem.ivaRate} onChange={e => setNewItem({ ...newItem, ivaRate: Number(e.target.value) })} sx={{ width: 80 }}>
                  <MenuItem value={0}>0%</MenuItem>
                  <MenuItem value={21}>21%</MenuItem>
                </TextField>
                <Button size="small" variant="contained" onClick={addItemToOpp} disabled={!newItem.productId}>Agregar</Button>
              </Stack>

              {/* Documentos adjuntos */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Documentos</Typography>
              <Stack spacing={1}>
                {[
                  { key: 'offer', label: 'Oferta', url: detailOpp.offerFileUrl },
                  { key: 'invoice', label: 'Factura', url: detailOpp.invoiceFileUrl },
                  { key: 'deliverySlip', label: 'Remito', url: detailOpp.deliverySlipFileUrl },
                ].map(doc => (
                  <Stack key={doc.key} direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ minWidth: 80 }}>{doc.label}:</Typography>
                    {doc.url ? (
                      <>
                        <Button size="small" onClick={async () => {
                          const blob = await api.downloadOpportunityFile(detailOpp.id, doc.key);
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url; a.download = `${doc.label}-${detailOpp.id}.pdf`; a.click();
                          window.URL.revokeObjectURL(url);
                        }}>Descargar</Button>
                        <Button size="small" color="error" onClick={async () => {
                          await api.deleteOpportunityFile(detailOpp.id, doc.key);
                          const refreshed = await api.listOpportunities({});
                          const updated = (refreshed.items || []).find(o => o.id === detailOpp.id);
                          if (updated) setDetailOpp(updated);
                        }}>Quitar</Button>
                      </>
                    ) : (
                      <Button size="small" variant="outlined" component="label">
                        Adjuntar PDF
                        <input hidden type="file" accept="application/pdf" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = async () => {
                            const base64 = (reader.result as string).split(',')[1] || '';
                            await api.uploadOpportunityFile(detailOpp.id, { fileType: doc.key, fileName: file.name, base64 });
                            const refreshed = await api.listOpportunities({});
                            const updated = (refreshed.items || []).find(o => o.id === detailOpp.id);
                            if (updated) setDetailOpp(updated);
                          };
                          reader.readAsDataURL(file);
                        }} />
                      </Button>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Save View Dialog */}
      <Dialog open={saveViewDialogOpen} onClose={() => setSaveViewDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Guardar vista</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre de la vista"
            value={newViewName}
            onChange={e => setNewViewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveViewConfirm(); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveViewDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveViewConfirm} variant="contained" disabled={!newViewName.trim()}>Guardar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};
